import { useCallback } from 'react';
import { useAsync, useId } from '@fluentui/react-hooks';
import { SnackbarType } from 'common/components/Notifications';
import { usePromise } from 'common/hooks/usePromise';
import { useNotifications } from 'store/layout/hooks/useNotifications';

export const useShareAction = <T>(action: () => Promise<T>, strings: { pending: string; success: string; failed: string }) => {
  const { showToast, dismissNotification } = useNotifications();
  const async = useAsync();
  const copyPromise = usePromise();
  const sharingToastId = useId('sharing-notification');
  const shareSuccessToastId = useId('share-success-notification');
  const shareFailedToastId = useId('share-failed-notification');

  return useCallback(() => {
    // show status after a delay in case the async operation is really fast
    const timeout = async.setTimeout(() => {
      showToast({
        id: sharingToastId,
        message: strings.pending,
        messageType: SnackbarType.info,
        dismissable: true,
        dismissDelayInMs: 0,
      });
    }, 500);

    copyPromise(action())
      .then(() => {
        showToast({
          id: shareSuccessToastId,
          message: strings.success,
          messageType: SnackbarType.success,
        });
      })
      .catch(() => {
        showToast({
          id: shareFailedToastId,
          message: strings.failed,
          messageType: SnackbarType.warning,
          hideIcon: true,
        });
      })
      .finally(() => {
        clearTimeout(timeout);
        dismissNotification(sharingToastId);
      });
  }, [async, copyPromise, action, showToast, sharingToastId, strings, shareSuccessToastId, shareFailedToastId, dismissNotification]);
};
